import { useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import * as jsyaml from "js-yaml";
const yamlDump = jsyaml.dump;
const yamlLoad = jsyaml.load;
import { Field, TextArea, PrimaryButton, GhostButton, ShareButton } from "@/tools/ui";

const OutBox = ({ value, testid }) => (
  <div className="rounded-xl border border-border bg-muted">
    <div className="flex items-center justify-between border-b border-border px-4 py-2">
      <span className="overline text-muted-foreground">Output</span>
      <div className="flex gap-2">
        <GhostButton onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }} className="px-3 py-1.5 text-xs"><Copy className="h-3 w-3" /> Copy</GhostButton>
        <ShareButton testid={`${testid}-share`} text={value} />
      </div>
    </div>
    <pre data-testid={testid} className="max-h-96 overflow-auto whitespace-pre-wrap break-words px-4 py-4 font-mono-tech text-sm">{value}</pre>
  </div>
);

const parseCsv = (text) => {
  const rows = text.trim().split(/\r?\n/).map((r) => r.split(","));
  const headers = rows.shift().map((h) => h.trim());
  return rows.map((r) => Object.fromEntries(headers.map((h, i) => [h, (r[i] ?? "").trim()])));
};

export const CsvToJson = () => {
  const [inp, setInp] = useState("name,age\nAda,36\nAlan,41");
  const [out, setOut] = useState("");
  const run = () => { try { setOut(JSON.stringify(parseCsv(inp), null, 2)); } catch { toast.error("Invalid CSV"); } };
  return (
    <div className="space-y-6">
      <Field label="CSV input"><TextArea data-testid="csv2json-input" rows={6} value={inp} onChange={(e) => setInp(e.target.value)} className="font-mono-tech" /></Field>
      <PrimaryButton data-testid="csv2json-run" onClick={run}>Convert to JSON</PrimaryButton>
      {out && <OutBox value={out} testid="csv2json-output" />}
    </div>
  );
};

export const JsonToCsv = () => {
  const [inp, setInp] = useState('[{"name":"Ada","age":36},{"name":"Alan","age":41}]');
  const [out, setOut] = useState("");
  const run = () => {
    try {
      const arr = JSON.parse(inp);
      if (!Array.isArray(arr)) throw new Error();
      const headers = [...new Set(arr.flatMap((o) => Object.keys(o)))];
      const lines = [headers.join(","), ...arr.map((o) => headers.map((h) => o[h] ?? "").join(","))];
      setOut(lines.join("\n"));
    } catch { toast.error("Provide a JSON array of objects"); }
  };
  return (
    <div className="space-y-6">
      <Field label="JSON array input"><TextArea data-testid="json2csv-input" rows={6} value={inp} onChange={(e) => setInp(e.target.value)} className="font-mono-tech" /></Field>
      <PrimaryButton data-testid="json2csv-run" onClick={run}>Convert to CSV</PrimaryButton>
      {out && <OutBox value={out} testid="json2csv-output" />}
    </div>
  );
};

export const JsonToYaml = () => {
  const [inp, setInp] = useState('{"app":"Kovi","tags":["fast","free"]}');
  const [out, setOut] = useState("");
  const run = () => { try { setOut(yamlDump(JSON.parse(inp))); } catch { toast.error("Invalid JSON"); } };
  return (
    <div className="space-y-6">
      <Field label="JSON input"><TextArea data-testid="json2yaml-input" rows={6} value={inp} onChange={(e) => setInp(e.target.value)} className="font-mono-tech" /></Field>
      <PrimaryButton data-testid="json2yaml-run" onClick={run}>Convert to YAML</PrimaryButton>
      {out && <OutBox value={out} testid="json2yaml-output" />}
    </div>
  );
};

export const YamlToJson = () => {
  const [inp, setInp] = useState("app: Kovi\ntags:\n  - fast\n  - free");
  const [out, setOut] = useState("");
  const run = () => { try { setOut(JSON.stringify(yamlLoad(inp), null, 2)); } catch { toast.error("Invalid YAML"); } };
  return (
    <div className="space-y-6">
      <Field label="YAML input"><TextArea data-testid="yaml2json-input" rows={6} value={inp} onChange={(e) => setInp(e.target.value)} className="font-mono-tech" /></Field>
      <PrimaryButton data-testid="yaml2json-run" onClick={run}>Convert to JSON</PrimaryButton>
      {out && <OutBox value={out} testid="yaml2json-output" />}
    </div>
  );
};
